import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Zap, Shield, Gauge, Coins, Save, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BattleBoostSliders({ agent, manaBalance, onUpdate }) {
  const [atkBoost, setAtkBoost] = useState(0);
  const [defBoost, setDefBoost] = useState(0);
  const [speedBoost, setSpeedBoost] = useState(0);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    loadSettings();
  }, [agent?.id]);

  const loadSettings = async () => {
    if (!agent?.id) return;
    
    try {
      const response = await axios.get(`${API}/battle/boost/${agent.id}`);
      if (response.data) {
        setAtkBoost(response.data.atk_boost || 0);
        setDefBoost(response.data.def_boost || 0);
        setSpeedBoost(response.data.speed_boost || 0);
      }
      setLoaded(true);
    } catch (error) {
      console.error("Failed to load boost settings:", error);
      setLoaded(true);
    }
  };

  const saveSettings = async () => {
    if (!agent?.id) return;
    
    setSaving(true);
    try {
      await axios.post(`${API}/battle/boost`, {
        agent_id: agent.id,
        atk_boost: atkBoost,
        def_boost: defBoost,
        speed_boost: speedBoost
      });
      toast.success("Battle boost settings saved!");
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const totalAllocation = atkBoost + defBoost + speedBoost;
  const estimatedManaCost = Math.floor(manaBalance * 0.001 * totalAllocation);
  const isOverAllocated = totalAllocation > 100;

  return (
    <Card data-testid="battle-boost-sliders" className="glass-card border-white/10">
      <CardHeader className="pb-2 px-3 sm:px-6">
        <CardTitle className="font-rajdhani text-lg sm:text-xl text-white flex items-center gap-2">
          <Zap className="text-yellow-400 w-5 h-5" />
          Battle Boost
        </CardTitle>
        <p className="text-xs text-slate-400">
          Spend $MANA for stat boosts
        </p>
      </CardHeader>
      <CardContent className="space-y-4 px-3 sm:px-6 pb-4">
        {/* MANA Cost Indicator */}
        <div className={`p-3 rounded-xl border ${isOverAllocated ? 'bg-red-900/20 border-red-500/50' : 'bg-yellow-900/20 border-yellow-500/30'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Coins className="text-yellow-400 w-4 h-4" />
              <span className="font-rajdhani text-xs sm:text-sm text-slate-300">Cost/Battle:</span>
            </div>
            <span className={`font-unbounded text-base sm:text-lg font-bold ${isOverAllocated ? 'text-red-400' : 'text-yellow-400'}`}>
              {estimatedManaCost.toLocaleString()}
            </span>
          </div>
          {isOverAllocated && (
            <div className="flex items-center gap-2 mt-2 text-red-400 text-xs">
              <AlertTriangle size={12} />
              <span>Max 100%</span>
            </div>
          )}
          <p className="text-[10px] text-slate-500 mt-1">
            Allocation: {totalAllocation}%
          </p>
        </div>

        {/* ATK Boost Slider */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <Zap className="text-red-400 w-4 h-4" />
              <span className="font-rajdhani text-sm text-white">ATK</span>
            </div>
            <span className="font-mono text-sm text-red-400 font-bold">{atkBoost}%</span>
          </div>
          <Slider
            data-testid="atk-boost-slider"
            value={[atkBoost]}
            onValueChange={([val]) => setAtkBoost(val)}
            max={100}
            step={5}
            className="cursor-pointer"
          />
        </div>

        {/* DEF Boost Slider */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <Shield className="text-blue-400 w-4 h-4" />
              <span className="font-rajdhani text-sm text-white">DEF</span>
            </div>
            <span className="font-mono text-sm text-blue-400 font-bold">{defBoost}%</span>
          </div>
          <Slider
            data-testid="def-boost-slider"
            value={[defBoost]}
            onValueChange={([val]) => setDefBoost(val)}
            max={100}
            step={5}
            className="cursor-pointer"
          />
        </div>

        {/* Speed Boost Slider */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <Gauge className="text-green-400 w-4 h-4" />
              <span className="font-rajdhani text-sm text-white">SPD</span>
            </div>
            <span className="font-mono text-sm text-green-400 font-bold">{speedBoost}%</span>
          </div>
          <Slider
            data-testid="speed-boost-slider"
            value={[speedBoost]}
            onValueChange={([val]) => setSpeedBoost(val)}
            max={100}
            step={5}
            className="cursor-pointer"
          />
        </div>

        {/* Save Button */}
        <Button
          data-testid="save-boost-btn"
          onClick={saveSettings}
          disabled={saving || isOverAllocated}
          className="w-full bg-gradient-to-r from-purple-600 to-cyan-600 text-white font-rajdhani rounded-full text-sm"
        >
          {saving ? (
            <>
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                className="mr-2"
              >
                <Save size={14} />
              </motion.div>
              Saving...
            </>
          ) : (
            <>
              <Save className="mr-2" size={14} />
              Save Strategy
            </>
          )}
        </Button>

        {/* Info - Compact */}
        <p className="text-[10px] text-slate-500 text-center">
          Boosts applied every battle • MANA spent win or lose
        </p>
      </CardContent>
    </Card>
  );
}
